import type { ChangeEvent, KeyboardEvent } from 'react';
import React, { useEffect, useId, useState } from 'react';
import Select from '../form/Select';
import { PAGE_SIZES } from './tablePreferencesStorage';

interface OrderAndPaginationControl {
    pageCount: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
}

interface PaginationProps {
    orderAndPaginationControl: OrderAndPaginationControl;
}

const barStyles =
    'border border-gray-300 bg-white px-3 py-2 text-sm leading-normal text-gray-500 hover:bg-gray-100 hover:text-gray-700 max-[400px]:px-1.5 max-[400px]:py-1.5 max-[400px]:text-xs';
const disabledButtonStyle = 'bg-gray-100';
const inputStyles =
    'w-12 border border-gray-300 px-2 py-2 text-center text-sm leading-normal focus:border-blue-500 focus:outline-none max-[400px]:w-8 max-[400px]:px-1 max-[400px]:py-1.5 max-[400px]:text-xs';

function Pagination({
    orderAndPaginationControl: { pageCount, currentPage, setCurrentPage, pageSize, setPageSize },
}: PaginationProps) {
    const pageSizeId = useId();
    const [inputValue, setInputValue] = useState<number | string>(currentPage);

    function handlePrevious(): void {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }

    function handleNext(): void {
        if (currentPage < pageCount) {
            setCurrentPage(currentPage + 1);
        }
    }

    function handleGoToPage(): void {
        const pageNumber = parseInt(String(inputValue), 10);
        if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= pageCount) {
            setCurrentPage(pageNumber);
        } else {
            // Reset input to current page if invalid.
            setInputValue(currentPage);
        }
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleGoToPage();
        }
    }

    function handleInputChange(e: ChangeEvent<HTMLInputElement>): void {
        const newValue = e.target.value;
        if (newValue === '') {
            setInputValue(newValue);
            return;
        }

        const pageNumber = parseInt(newValue, 10);
        if (Number.isNaN(pageNumber)) {
            return;
        }

        setInputValue(Math.max(1, pageNumber));
    }

    // Update input value when currentPage changes.
    useEffect(() => {
        setInputValue(currentPage);
    }, [currentPage]);

    if (pageCount === 0) return null;

    return (
        <div className="inline-flex max-w-full flex-wrap items-center justify-end gap-2 py-1 align-middle">
            <div className="flex items-center gap-2 text-sm font-normal">
                <label htmlFor={pageSizeId} className="whitespace-nowrap text-gray-400">
                    {gettext('ROWS')}
                </label>
                <Select
                    id={pageSizeId}
                    small
                    value={pageSize}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                    data={PAGE_SIZES.map((size) => [String(size), String(size)])}
                />
            </div>
            {pageCount > 1 && (
                <div className="inline-flex max-w-full items-center justify-center -space-x-px">
                    <button
                        disabled={currentPage === 1}
                        onClick={handlePrevious}
                        className={`ml-0 rounded-l-lg ${barStyles} ${
                            currentPage === 1 && disabledButtonStyle
                        } max-[400px]:max-w-[5.5rem] max-[400px]:truncate`}
                    >
                        {gettext('PREVIOUS')}
                    </button>
                    <div className="flex items-center -space-x-px">
                        <span className={`${barStyles} whitespace-nowrap`}>{gettext('PAGE')}</span>
                        <input
                            type="number"
                            min={1}
                            step={1}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            className={inputStyles}
                            aria-label={gettext('PAGE_NUMBER')}
                        />
                        <span className={`${barStyles} whitespace-nowrap`}>
                            {gettext('OF_TOTAL')} {pageCount}
                        </span>
                        <button
                            onClick={handleGoToPage}
                            className={barStyles}
                            disabled={
                                !inputValue || parseInt(String(inputValue), 10) === currentPage
                            }
                        >
                            {gettext('GO')}
                        </button>
                    </div>
                    <button
                        disabled={currentPage === pageCount}
                        onClick={handleNext}
                        className={`rounded-r-lg ${barStyles} ${
                            currentPage === pageCount && disabledButtonStyle
                        } max-[400px]:max-w-[5.5rem] max-[400px]:truncate`}
                    >
                        {gettext('NEXT')}
                    </button>
                </div>
            )}
        </div>
    );
}

export default Pagination;
